import { LoadingPage, useToast } from "@qubit/autoparts";
import { ReactNode, useEffect, useState } from "react";

import { Outlet, useLocation, Navigate, useNavigate } from "react-router-dom";

import { ClientConfigFailedDialog } from "~/components/settings/ClientConfigFailedDialog";
import useFlag from "~/config/flags";
import HandRaiseReminderModal from "~/features/login/HandRaiseReminderModal";
import useModeUpdate from "~/hooks/useModeUpdate";

import { useSendInitialPongOnce } from "~/hooks/useSendInitialPongOnce";
import {
  selectAuthHeaderReady,
  selectClientConfigReady,
  selectClientConfigError,
  selectFlagsReadyClientId
} from "~/redux/selectors/appSelectors";

import { selectIsUserLoggedIn } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  useLazyGetAndonStateQuery,
  useToggleHandRaiseMutation
} from "~/redux/warehouse/andon.hooks";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { clientConfigApi } from "~/redux/warehouse/clientConfig";

import { clearClientConfigError, setSignalREnabled } from "./app.slice";
import { settingsPathAllowsNoFulfillmentCenter } from "./privateRoute.utils";
import { useAppDispatch, useAppSelector } from "./store";

type PrivateRouteProps = {
  children?: ReactNode;
};

export function PrivateRoute({ children }: PrivateRouteProps): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const normalizedPath = location.pathname.toLowerCase();
  const isSettingsPathAllowingNoFc =
    settingsPathAllowsNoFulfillmentCenter(normalizedPath);
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const loginPath =
    useAppSelector((state) => state.app.siteLoginPath) || "/login";

  const isAuthHeaderReady = useAppSelector(selectAuthHeaderReady);
  const clientConfigReady = useAppSelector(selectClientConfigReady);
  const clientConfigError = useAppSelector(selectClientConfigError);
  const isFlagsReady = useAppSelector(selectFlagsReadyClientId);

  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const { enableSideNavSubMenus, improvedErrorHandlingConfig } = useFlag();
  const [showHandRaiseModal, setShowHandRaiseModal] = useState(false);
  const [initialHandRaiseCheckDone, setInitialHandRaiseCheckDone] =
    useState(false);

  const [toggleHandRaise] = useToggleHandRaiseMutation();
  const [triggerGetClientConfig] =
    clientConfigApi.useLazyGetClientConfigQuery();
  const { errorToast } = useToast();

  const { data: autostoreGrids, isLoading: autostoreGridsLoading } =
    useGetAutostoreGridsQuery(undefined, {
      skip: !usersFulfillmentCenter
    });

  const [triggerGetAndon] = useLazyGetAndonStateQuery();

  const siteWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const autostoreGridId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.autostoreGridId
  );

  // Trigger andon state queries for each grid
  useEffect(() => {
    if (autostoreGridsLoading) return;

    // skip handcheck if fc or workstation is not selected
    if (!autostoreGrids || !siteWorkstationId || !usersFulfillmentCenter) {
      setInitialHandRaiseCheckDone(true);
      return;
    }

    const handRaisedCheck = async () => {
      try {
        const getAndonPromises = autostoreGrids.map((grid) =>
          triggerGetAndon({ autostoreGridId: grid.autostoreGridId })
        );

        const allAndonResults = await Promise.all(getAndonPromises);

        const isHandRaised = allAndonResults.some((result) => {
          const andonData = result.data;
          if (!andonData) return false;

          // Check if this specific workstation has a raised hand in the response
          // API response structure: { gridId: string, workstations: [...] }
          const workstationData = andonData.workstations?.find(
            (ws: { workstationId: string; handRaised: boolean }) =>
              ws.workstationId === siteWorkstationId
          );
          return workstationData?.handRaised === true;
        });

        // This is the initial check after login/page load
        setInitialHandRaiseCheckDone(true);

        if (isHandRaised) {
          // Hand was already raised when user logged in - show reminder
          setShowHandRaiseModal(true);
        }
      } catch (error) {
        console.error("Failed to load andon data:", error);
        // Still set states so we don't block the UI indefinitely
        setInitialHandRaiseCheckDone(true);
      }
    };

    void handRaisedCheck();
  }, [
    autostoreGrids,
    autostoreGridsLoading,
    triggerGetAndon,
    siteWorkstationId,
    usersFulfillmentCenter
  ]);

  useModeUpdate({ pathname: location.pathname });
  useSendInitialPongOnce();

  // enable signalR if the user is logged in and has a fulfillment center
  const signalREnabled = useAppSelector((state) => state.app.signalREnabled);

  // If the user is logged in and has a fulfillment center, enable SignalR
  useEffect(() => {
    if (
      isUserLoggedIn &&
      usersFulfillmentCenter?.fulfillmentCenterId &&
      !signalREnabled
    ) {
      dispatch(setSignalREnabled(true));
    }
  }, [isUserLoggedIn, usersFulfillmentCenter, signalREnabled, dispatch]);

  if (!isUserLoggedIn) {
    return <Navigate to={loginPath} replace />;
  }

  if (clientConfigError) {
    if (!improvedErrorHandlingConfig) {
      return <Navigate to="/logout" replace />;
    }

    return (
      <>
        <LoadingPage loadingMessage="Loading..." />
        <ClientConfigFailedDialog
          open
          onRetry={() => {
            dispatch(clearClientConfigError());
            void triggerGetClientConfig();
          }}
          onGoToLogout={() => {
            navigate("/logout", { replace: true });
          }}
        />
      </>
    );
  }

  // Wait until configuration and flags are ready.
  if (!isAuthHeaderReady || !clientConfigReady || !isFlagsReady) {
    return <LoadingPage loadingMessage="Loading..." />;
  }

  // If the user doesn't have a fulfillment center and is not on settings, redirect.
  if (!usersFulfillmentCenter && !isSettingsPathAllowingNoFc) {
    return (
      <Navigate
        to={enableSideNavSubMenus ? "/settings/general" : "/settings"}
        replace
      />
    );
  }

  if (showHandRaiseModal) {
    return (
      <>
        <HandRaiseReminderModal
          variant="login"
          onLowerHand={async () => {
            if (siteWorkstationId && autostoreGridId) {
              try {
                await toggleHandRaise({
                  workstationId: siteWorkstationId,
                  autostoreGridId,
                  raiseHand: false
                });
              } catch {
                errorToast("Unable to lower hand");
              }
            }
            setShowHandRaiseModal(false);
          }}
          onKeepHandRaised={() => {
            setShowHandRaiseModal(false);
          }}
        />
      </>
    );
  }

  // Show loading until we've completed the initial hand raise check
  if (!initialHandRaiseCheckDone) {
    return <LoadingPage loadingMessage="Loading..." />;
  }

  return children ? <>{children}</> : <Outlet />;
}
